/**
 * Bootstrap — the resolved addresses a client is constructed from, and the one
 * sanctioned construction path that resolves them first (sdk-source-of-truth
 * sdk ADR-008).
 *
 * Two resolvers own the precedence, one per runtime rung:
 *   - Node (`resolveNodeBootstrap` in `./discovery.js`): env > discovery file >
 *     daemon endpoints; daemon absence is warn-only, backend absence is fatal.
 *   - Browser (here): an injected host channel is authoritative and verbatim;
 *     else a declared runtime config's `backendUrl`, else `VITE_BACKEND_URL`
 *     when non-blank, else the page origin, and the daemon is null — the
 *     web-only arm. A local-daemon probe that would let a
 *     plain browser tab adopt a daemon is gated on named Rust-side follow-up
 *     work and is deliberately not implemented here.
 *
 * `createSDK` is the only path that also attaches the device identity (the
 * per-request `X-Device-Id` + registration precondition). `new
 * LocalAgentSDK(config)` stays public and unchanged for the auth-only /
 * config-only shape — its wire behaviour is identical to a build without this
 * module.
 */

import { LocalAgentSDK } from './client.js';
import type { SDKConfig } from './http.js';
import type { RetryConfig } from './http.js';
import type { AuthProvider } from './auth/types.js';
import type { WebSocketFactory } from './chat/types.js';

/** A host looked for a daemon and did not find one (sdk ADR-031). */
export type DaemonUnresolved = { readonly unresolved: true };

export const DAEMON_UNRESOLVED: DaemonUnresolved = { unresolved: true };

/** A daemon URL, `null` for a host deliberately running without one, or
 *  `DAEMON_UNRESOLVED` for a host that expected one (sdk ADR-031). */
export type DaemonAddress = string | null | DaemonUnresolved;

/** The realm stem the web arm reports; one browser profile is one realm. */
export const WEB_REALM = 'web';

/** The only address-shaped value a host ever hands the SDK. */
export interface SdkBootstrap {
  /** The cloud API base. Required, never empty — a bad one fails the boot. */
  backendUrl: string;
  daemonUrl: DaemonAddress;
  /** The correlating dimension on lifecycle log events (sdk ADR-027). */
  realm?: string;
  /** Host-configured {@link SDKConfig.historyPageSize}; absent = the SDK default. */
  historyPageSize?: number;
  /** Host-configured {@link SDKConfig.historyWindowCeiling}; absent = the SDK default. */
  historyWindowCeiling?: number;
}

/** The sentinel survives a structured-clone IPC hop, so it is read by shape. */
export function isDaemonUnresolved(address: DaemonAddress): address is DaemonUnresolved {
  return typeof address === 'object' && address !== null;
}

/** Whether this bootstrap names a daemon to talk to (sdk ADR-031). */
export function hasDaemonAddress(
  bootstrap: SdkBootstrap,
): bootstrap is SdkBootstrap & { daemonUrl: string } {
  return typeof bootstrap.daemonUrl === 'string';
}

/** The realm stem this bootstrap names (sdk ADR-027). */
export function bootstrapRealm(bootstrap: SdkBootstrap): string {
  const realm = bootstrap.realm?.trim();
  if (!realm) {
    throw new Error(
      'bootstrap: the host named no realm — the lifecycle log’s correlating dimension is threaded, never derived',
    );
  }
  return realm;
}

/**
 * The one browser-side input that cannot be derived in-page: how a sandboxed
 * Electron renderer receives the main process's resolved bootstrap. A transport
 * carrying no policy — the same category as `webSocketFactory` and
 * `openExternal`. The web app supplies nothing; its absence IS the web arm.
 */
export type HostBootstrapChannel = () => Promise<SdkBootstrap>;

function requireBackendUrl(raw: string | undefined | null, source: string): string {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!trimmed) {
    throw new Error(`bootstrap: ${source} produced no backendUrl — refusing to configure an SDK without an address`);
  }
  return trimmed;
}

/**
 * The deployment document a build-once web bundle reads at boot instead of
 * baking per-environment values in (la ADR-8588f,
 * la ADR-d43ea). Served beside the bundle; every field is required.
 */
export interface WebRuntimeConfig {
  /** The API origin — a different host from the page on a two-host stack. */
  readonly backendUrl: string;
  readonly stytchPublicToken: string;
  readonly stripePublishableKey: string;
  /**
   * The desktop release channel base that serves `manifest.json` and the installers, or `null`
   * when the document predates the key: during a rollout an older pod can serve this document to
   * a newer bundle, so the key is read where it is used, never required at boot (la ADR-6ff8f).
   */
  readonly desktopDownloadsUrl: string | null;
}

const runtimeConfigLoads = new Map<string, Promise<WebRuntimeConfig>>();

function requireConfigString(doc: Record<string, unknown>, key: keyof WebRuntimeConfig, url: string): string {
  const value = doc[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`web runtime config ${url}: \`${key}\` is missing or blank`);
  }
  return value.trim();
}

function requireConfigHttpUrl(doc: Record<string, unknown>, key: keyof WebRuntimeConfig, url: string): string {
  const value = requireConfigString(doc, key, url);
  if (!/^https?:\/\/[^/\s]+/i.test(value)) {
    throw new Error(`web runtime config ${url}: ${key} ${value} is not an http(s) URL`);
  }
  return value.replace(/\/+$/, '');
}

async function fetchWebRuntimeConfig(url: string): Promise<WebRuntimeConfig> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`web runtime config ${url}: HTTP ${response.status}`);
  }
  const doc: unknown = await response.json();
  if (typeof doc !== 'object' || doc === null || Array.isArray(doc)) {
    throw new Error(`web runtime config ${url}: not a JSON object`);
  }
  const fields = doc as Record<string, unknown>;
  return {
    backendUrl: requireConfigHttpUrl(fields, 'backendUrl', url),
    stytchPublicToken: requireConfigString(fields, 'stytchPublicToken', url),
    stripePublishableKey: requireConfigString(fields, 'stripePublishableKey', url),
    desktopDownloadsUrl:
      fields.desktopDownloadsUrl === undefined ? null : requireConfigHttpUrl(fields, 'desktopDownloadsUrl', url),
  };
}

/**
 * Fetch and validate the runtime config at `url`, once per realm: the web host
 * awaits it before render (for its public keys) and the browser resolver reads
 * the same load. A failed load stays failed — a reload is the retry.
 */
export function loadWebRuntimeConfig(url: string): Promise<WebRuntimeConfig> {
  const existing = runtimeConfigLoads.get(url);
  if (existing) {
    return existing;
  }
  const load = fetchWebRuntimeConfig(url);
  runtimeConfigLoads.set(url, load);
  return load;
}

/** What the web arm resolves from: build-time declarations and the page. */
export interface WebArmSources {
  /** `VITE_WEB_RUNTIME_CONFIG` — the runtime config path a build-once bundle declares. */
  runtimeConfig?: string;
  /** `VITE_BACKEND_URL` — an address baked at build time. */
  backendUrl?: string;
  pageOrigin?: string;
}

function nonBlank(value: string | undefined): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/** The absolute URL of the runtime config the sources declare, or null when they declare none. */
export function declaredWebRuntimeConfigUrl(sources: WebArmSources): string | null {
  const declared = nonBlank(sources.runtimeConfig);
  return declared === null ? null : new URL(declared, sources.pageOrigin).toString();
}

/**
 * The web arm (no host channel). A declared runtime config is the only source
 * of the address — a failed or incomplete config fails the boot, never the page
 * origin (la ADR-8588f). Otherwise `VITE_BACKEND_URL`
 * wins when non-blank, else the page origin. The daemon is null.
 */
export async function resolveWebArmBootstrap(sources: WebArmSources): Promise<SdkBootstrap> {
  const runtimeConfigUrl = declaredWebRuntimeConfigUrl(sources);
  if (runtimeConfigUrl !== null) {
    const config = await loadWebRuntimeConfig(runtimeConfigUrl);
    return { backendUrl: config.backendUrl, daemonUrl: null, realm: WEB_REALM };
  }
  const backendUrl = nonBlank(sources.backendUrl) ?? nonBlank(sources.pageOrigin);
  if (backendUrl === null) {
    throw new Error(
      'bootstrap: no page origin and no VITE_BACKEND_URL — cannot derive a backend URL',
    );
  }
  return { backendUrl, daemonUrl: null, realm: WEB_REALM };
}

/** The web arm's sources as this bundle and page supply them. */
export function browserWebArmSources(): WebArmSources {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
  return {
    runtimeConfig: env.VITE_WEB_RUNTIME_CONFIG,
    backendUrl: env.VITE_BACKEND_URL,
    pageOrigin: (globalThis as { location?: { origin: string } }).location?.origin,
  };
}

/**
 * Browser-rung precedence. A supplied channel is authoritative: its value is
 * returned verbatim and its rejection propagates unchanged — the channel is a
 * transport, and a failed main-process resolution must fail the boot, not fall
 * back. With no channel (the web app), the web arm resolves from this bundle's
 * declarations (`resolveWebArmBootstrap`).
 */
export async function resolveBrowserBootstrap(
  channel?: HostBootstrapChannel,
): Promise<SdkBootstrap> {
  if (channel) {
    const resolved = await channel();
    if (resolved?.daemonUrl === undefined) {
      throw new Error(
        'bootstrap: the host bootstrap channel named no daemonUrl — a host says whether it runs without a daemon, it is never inferred (sdk ADR-031)',
      );
    }
    return {
      backendUrl: requireBackendUrl(resolved.backendUrl, 'the host bootstrap channel'),
      daemonUrl: resolved.daemonUrl,
      realm: resolved.realm,
      historyPageSize: resolved.historyPageSize,
      historyWindowCeiling: resolved.historyWindowCeiling,
    };
  }
  return resolveWebArmBootstrap(browserWebArmSources());
}

/** Everything `createSDK` needs after the addresses are resolved. */
export interface CreateSDKOptions {
  bootstrap: SdkBootstrap;
  auth?: AuthProvider;
  webSocketFactory?: WebSocketFactory;
  openExternal?: (url: string) => Promise<void>;
  /** Operator pin — e2e seed context, Storybook. Wins over the daemon answer. */
  deviceId?: string;
  timeout?: number;
  retry?: RetryConfig | false;
  /** The credential-holder configuration, forwarded verbatim (sdk ADR-010). */
  credential?: SDKConfig['credential'];
}

/**
 * Construct the client from an already-resolved bootstrap. Synchronous once the
 * bootstrap is in hand — the async work belongs to the resolvers, which is what
 * makes `hasDaemon`/`baseUrl` correct from first render and the construct-once
 * rule expressible (sdk ADR-008).
 */
export function createSDK(init: CreateSDKOptions): LocalAgentSDK {
  const { bootstrap } = init;
  if (!bootstrap.backendUrl) {
    throw new Error('createSDK: bootstrap.backendUrl is required (no fallback to localhost)');
  }
  if (isDaemonUnresolved(bootstrap.daemonUrl)) {
    throw new Error(
      'createSDK: the daemon address is unresolved — the host refuses that boot, it does not construct a client (sdk ADR-031)',
    );
  }
  const config: SDKConfig & { shogunchopUrl?: string } = {
    baseUrl: bootstrap.backendUrl,
    shogunchopUrl: hasDaemonAddress(bootstrap) ? bootstrap.daemonUrl : undefined,
    deviceId: init.deviceId,
    auth: init.auth,
    timeout: init.timeout,
    retry: init.retry,
    webSocketFactory: init.webSocketFactory,
    openExternal: init.openExternal,
    credential: init.credential,
    historyPageSize: bootstrap.historyPageSize,
    historyWindowCeiling: bootstrap.historyWindowCeiling,
  };
  const sdk = new LocalAgentSDK(config);
  sdk.useDeviceIdentity();
  return sdk;
}
